import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  RefreshCw, ChevronLeft, CheckCircle, XCircle, 
  SkipForward, AlertTriangle, Clock
} from "lucide-react";
import { getDigestDeliveries } from "../api/slackDigest";

const useBrandId = () => "b_123"; // Replace with session

function StatusBadge({ status, skipReason }) {
  const config = {
    posted: { 
      className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      icon: CheckCircle,
      label: "Posted"
    },
    skipped: { 
      className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      icon: SkipForward,
      label: skipReason === "skipped_no_change" ? "Skipped (no change)" : "Skipped"
    },
    failed: { 
      className: "bg-rose-500/20 text-rose-400 border-rose-500/30",
      icon: XCircle,
      label: "Failed"
    }
  };

  const { className, icon: Icon, label } = config[status] || config.failed;

  return (
    <Badge className={`flex items-center gap-1.5 ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
}

export default function SlackDigestDeliveriesPage() {
  const brandId = useBrandId();
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState({ posted: 0, skipped: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(null); // null = all

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await getDigestDeliveries(brandId, 50, filter);
      setDeliveries(res.deliveries || []);
      setStats(res.stats || { posted: 0, skipped: 0, failed: 0 });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [filter]);

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-100 p-6" data-testid="digest-deliveries-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link
              to="/dashboard/settings/integrations/slack"
              className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Slack Settings
            </Link>
            <h1 className="text-2xl font-semibold tracking-tight">Digest Delivery Log</h1>
            <p className="text-sm text-slate-300/80 mt-1">
              Verify that weekly digests are being delivered reliably. Delta-gated digests only send when there's meaningful change.
            </p>
          </div>
          <Button
            variant="outline"
            className="bg-white/5 border-white/10 hover:bg-white/10"
            onClick={load}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card 
            className={`bg-white/5 border-white/10 cursor-pointer transition hover:bg-white/10 ${filter === 'posted' ? 'ring-2 ring-emerald-500/50' : ''}`}
            onClick={() => setFilter(filter === 'posted' ? null : 'posted')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.posted}</div>
                  <div className="text-xs text-slate-400">Posted</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`bg-white/5 border-white/10 cursor-pointer transition hover:bg-white/10 ${filter === 'skipped' ? 'ring-2 ring-amber-500/50' : ''}`}
            onClick={() => setFilter(filter === 'skipped' ? null : 'skipped')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <SkipForward className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.skipped}</div>
                  <div className="text-xs text-slate-400">Skipped (no change)</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`bg-white/5 border-white/10 cursor-pointer transition hover:bg-white/10 ${filter === 'failed' ? 'ring-2 ring-rose-500/50' : ''}`}
            onClick={() => setFilter(filter === 'failed' ? null : 'failed')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-rose-500/20 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-rose-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.failed}</div>
                  <div className="text-xs text-slate-400">Failed</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter indicator */}
        {filter && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-slate-400">Filtering by:</span>
            <Badge className="bg-white/10 text-white border-white/20">{filter}</Badge>
            <button 
              className="text-sm text-sky-400 hover:underline"
              onClick={() => setFilter(null)}
            >
              Clear filter
            </button>
          </div>
        )}

        {/* Deliveries Table */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg">Delivery History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-400">Loading...</div>
            ) : error ? (
              <div className="text-center py-8 text-rose-400">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                {error}
              </div>
            ) : deliveries.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No deliveries logged yet.
                <p className="text-xs mt-2">Digests are sent weekly when there's meaningful change.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-xs font-medium text-slate-400 py-3 px-4">When</th>
                      <th className="text-left text-xs font-medium text-slate-400 py-3 px-4">Status</th>
                      <th className="text-left text-xs font-medium text-slate-400 py-3 px-4">Snapshot</th>
                      <th className="text-left text-xs font-medium text-slate-400 py-3 px-4">Reason/Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map((d) => (
                      <tr key={d.id} className="border-t border-white/5 hover:bg-white/5 transition">
                        <td className="py-4 px-4 text-slate-200/80">
                          {d.attempted_at ? new Date(d.attempted_at).toLocaleString() : "—"}
                        </td>
                        <td className="py-4 px-4">
                          <StatusBadge status={d.status} skipReason={d.skip_reason} />
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-xs text-slate-300/70">
                            Score: <span className="text-white font-medium">{d.summary?.score ?? "—"}</span>
                          </div>
                          <div className="text-xs text-slate-300/70">
                            Driver: <span className="text-white font-medium">{d.summary?.top_driver ?? "—"}</span>
                          </div>
                          <div className="text-xs text-slate-300/70">
                            Loss: <span className="text-white font-medium">${d.summary?.monthly_loss ?? "—"}</span>
                          </div>
                          {d.summary?.reasons && d.summary.reasons.length > 0 && (
                            <div className="text-xs text-emerald-400/80 mt-1">
                              Sent because: {d.summary.reasons.join(", ")}
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {d.status === "failed" && (
                            <span className="text-rose-400 text-xs">{d.error || "Unknown error"}</span>
                          )}
                          {d.status === "skipped" && (
                            <span className="text-amber-400 text-xs">
                              {d.skip_reason === "skipped_no_change" 
                                ? "No meaningful changes detected" 
                                : d.skip_reason === "skipped_already_sent_this_week"
                                  ? "Already sent this week"
                                  : d.skip_reason || "Skipped"}
                            </span>
                          )}
                          {d.status === "posted" && d.slack_ts && (
                            <span className="text-slate-400 text-xs">ts: {d.slack_ts}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help text */}
        <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-xl">
          <h3 className="text-sm font-medium text-white mb-2">Understanding Digest Delivery</h3>
          <ul className="text-xs text-slate-300/80 space-y-1">
            <li>• <strong className="text-white">Posted:</strong> Digest was sent to Slack because meaningful change was detected</li>
            <li>• <strong className="text-white">Skipped (no change):</strong> Delta gating prevented spam — no significant changes since last digest</li>
            <li>• <strong className="text-white">Failed:</strong> Check Slack token validity and channel permissions</li>
          </ul>
          <p className="text-xs text-slate-400 mt-3">
            Triggers for sending: score changed ≥1.0, top driver changed, leakage changed ≥10%, high severity events, or pending approvals exceeded threshold.
          </p>
        </div>
      </div>
    </div>
  );
}
